/**
 * Sticky mobile call to action for the home page.
 * Shown only on mobile, once the hero has scrolled out of view.
 * Hidden again while the footer or any accordion row in the main content is on screen,
 * so it never covers the footer or the FAQ controls.
 */
if (!customElements.get('sustena-sticky-cta')) {
  class SustenaStickyCta extends HTMLElement {
    #mobile = window.matchMedia('(max-width: 749px)');
    /** @type {IntersectionObserver | null} */
    #observer = null;
    /** @type {Element | null} */
    #hero = null;
    #pastHero = false;
    /** @type {Set<Element>} */
    #blockersInView = new Set();

    connectedCallback() {
      this.#mobile.addEventListener('change', this.#update);
      document.addEventListener('shopify:section:load', this.#refresh);
      document.addEventListener('shopify:section:unload', this.#refresh);
      this.#observe();
    }

    disconnectedCallback() {
      this.#mobile.removeEventListener('change', this.#update);
      document.removeEventListener('shopify:section:load', this.#refresh);
      document.removeEventListener('shopify:section:unload', this.#refresh);
      this.#observer?.disconnect();
    }

    #refresh = () => {
      // Wait for the theme editor to finish swapping the section markup.
      requestAnimationFrame(() => this.#observe());
    };

    #observe() {
      this.#observer?.disconnect();
      this.#blockersInView.clear();

      const main = document.getElementById('MainContent');
      // Without the hero section, fall back to the first section of the page.
      this.#hero =
        document.querySelector('[data-sustena-hero]') ??
        Array.from(main?.children ?? []).find((section) => !section.contains(this)) ??
        null;
      this.#pastHero = !this.#hero;

      const blockers = [
        ...document.querySelectorAll('body footer'),
        ...(main?.querySelectorAll('details') ?? []),
      ];

      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === this.#hero) {
            this.#pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          } else if (entry.isIntersecting) {
            this.#blockersInView.add(entry.target);
          } else {
            this.#blockersInView.delete(entry.target);
          }
        }
        this.#update();
      });

      if (this.#hero) this.#observer.observe(this.#hero);
      for (const blocker of blockers) this.#observer.observe(blocker);
      this.#update();
    }

    #update = () => {
      const visible = this.#mobile.matches && this.#pastHero && this.#blockersInView.size === 0;
      this.classList.toggle('is-visible', visible);
      this.inert = !visible;
    };
  }

  customElements.define('sustena-sticky-cta', SustenaStickyCta);
}
